import { Component, inject, input, output } from '@angular/core';
import { Booking } from '../../../../core/models/entities/booking';
import { BookingsService } from '../../../../core/services/bookings.service';
import { ModalService } from '../../../../core/services/modal.service';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { take } from 'rxjs/operators';
import { ToastService } from '../../../../core/services/toast.service';
import { ApiError } from '../../../../core/models/common/api-error';
import { CancelConditionsHelper } from '../../../../core/helpers/cancel-conditions.helper';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-booking-cancellation-section',
  template: `
    <div>
      <h3 class="mb-4">{{ 'booking-cancellation.conditions-title' | transloco }}</h3>
      @for (summaryLine of cancelConditionsSummaryLines; track summaryLine) {
        <div class="mb-2">
          {{ summaryLine }}
        </div>
      }
      @if (booking()?.room?.vendor?.general_terms_public_url) {
        <a
          class="mt-4 block underline"
          [href]="booking()?.room?.vendor?.general_terms_public_url"
          target="_blank"
        >
          {{ 'booking-cancellation.see-general-terms' | transloco }}
        </a>
      }

      <div class="mt-6">
        <app-button
          (click)="handleCancellationConfirmation()"
          variant="secondary"
          data-cy="cancel-button"
          size="sm"
        >
          {{ 'booking-cancellation.cancel-button' | transloco }}
        </app-button>
      </div>
    </div>
  `,
  standalone: false,
})
export class BookingCancellationSectionComponent {
  booking = input.required<Booking>();
  token = input.required<string>();

  bookingCancelled = output<void>();

  private bookingsService = inject(BookingsService);
  private modalService = inject(ModalService);
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);

  handleCancellationConfirmation() {
    const instance = this.modalService.open(ConfirmModalComponent, {
      title: this.translocoService.translate('booking-cancellation.modal-title'),
      message: this.translocoService.translate('booking-cancellation.modal-message'),
      okAction: {
        title: this.translocoService.translate('booking-cancellation.modal-ok'),
        variant: 'danger',
      },
      cancelAction: {
        title: this.translocoService.translate('booking-cancellation.modal-cancel'),
      },
    });
    instance.okClicked$.pipe(take(1)).subscribe(async () => {
      try {
        instance.saving = true;
        await this.bookingsService.cancelByToken(this.token());
        instance.saving = false;

        // Fermer la modal avant d'afficher le toast
        this.modalService.close();

        this.toastService.success(
          this.translocoService.translate('booking-cancellation.success-toast'),
        );
        this.bookingCancelled.emit();
      } catch (e) {
        instance.saving = false;
        this.toastService.error((e as ApiError).message);
      }
    });
  }

  get cancelConditionsSummaryLines() {
    return CancelConditionsHelper.getCancelConditionLines(
      this.booking()?.room?.vendor?.cancel_conditions ?? [],
      this.translocoService,
    );
  }
}
